import { useState, useEffect } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Snackbar, Alert } from "@mui/material";
import { routes } from "src/constants/routes.module";
import { useAppSelector } from "src/redux/store";

type PrivateRouteProps = {
  element: () => JSX.Element;
  path?: string;
  exact?: boolean;
  index?: boolean;
  isAuthRoute?: boolean;
};

const PrivateRoute = ({
  element: Component,
  isAuthRoute = false,
  path = "/",
  exact = false,
  index = false,
  ...rest
}: PrivateRouteProps) => {
  const { isLoggedIn, data } = useAppSelector((state) => state.root.user);
  const location = useLocation();
  const navigate = useNavigate();
  const [showAlert, setShowAlert] = useState(false);
  const [shouldNavigate, setShouldNavigate] = useState(false);

  // Use actual location pathname to check current page
  const currentPath = location.pathname;

  // Check if user is an Organization and hasn't completed GCA profile
  const isOrganizationWithoutGCA =
    data?.userType === "ORGANIZATION" && data?.isGCAEntity === false;

  // Allow access to GCA view page and auth routes, block all other pages
  const isGCAViewPage = currentPath?.includes("/gca/view");
  const isAllowedWithoutGCA = isGCAViewPage || isAuthRoute;

  // Show alert when trying to access restricted pages
  useEffect(() => {
    if (isOrganizationWithoutGCA && !isAllowedWithoutGCA) {
      setShowAlert(true);
    }
  }, [isOrganizationWithoutGCA, isAllowedWithoutGCA, currentPath]);

  // Handle navigation after user closes alert or timeout
  useEffect(() => {
    if (shouldNavigate && data?.userId) {
      navigate(`/gca/view/${data.userId}`);
    }
  }, [shouldNavigate, data?.userId, navigate]);

  const handleCloseAlert = () => {
    setShowAlert(false);
    setShouldNavigate(true);
  };

  if (!isLoggedIn) {
    return <Navigate to={routes.signIn.path} />;
  }

  if (isOrganizationWithoutGCA && !isAllowedWithoutGCA) {
    return (
      <Snackbar
        open={showAlert}
        autoHideDuration={4000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseAlert}
          severity="warning"
          sx={{ width: "100%" }}
        >
          Please complete your personal details to access this page. Click the
          "Edit" button to get started.
        </Alert>
      </Snackbar>
    );
  }

  return <Component {...rest} />;
};

export default PrivateRoute;
